import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { InternalPage } from "../../../components/primitives/layout/InternalPage";
import { PageHeader } from "../../../components/primitives/layout/PageHeader";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import { Note } from "../../../components/primitives/data-display/Note";
import { ConfirmDialog } from "../../../components/primitives/feedback/ConfirmDialog";
import { BatchJobStatusBadge } from "../../../components/domain/data-display/BatchJobStatusBadge";
import { RequireRole } from "../../../components/gate/RequireRole";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import { BATCH_JOB_KIND_VIEWS } from "./batch_job_kinds";
import { formatDuration } from "../../../lib/date";
import { useBatchJobDetail } from "./useBatchJobDetail";
import { ROUTES, feedPath } from "../../../route_paths";

const CANCELLABLE_STATUSES = new Set(["queued", "running", "paused"]);
const PAUSABLE_STATUSES = new Set(["queued", "running"]);

/** Header (kind, status, progress, created/finished, admin Pause/Resume and Cancel) plus the kind-specific results
 *  panel for one batch job. Polls while the job is still queued/running/paused. */
export default function BatchJobDetail() {
  const {
    job,
    loading,
    error,
    cancelDialogProps,
    onCancel,
    onPause,
    onResume,
    onDelete,
    onRetryFailed,
    refresh,
  } = useBatchJobDetail();

  if (loading) return <LoadingState />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!job) return null;

  const done = job.itemCount - job.openItemCount;
  const progress = job.itemCount === 0 ? 0 : (done / job.itemCount) * 100;
  const { label, Panel } = BATCH_JOB_KIND_VIEWS[job.kind];
  const retryable =
    job.failedItemCount > 0 && (job.status === "completed" || CANCELLABLE_STATUSES.has(job.status));
  const deletable = !job.deletedAt && !CANCELLABLE_STATUSES.has(job.status);

  return (
    <InternalPage width="full">
      <PageHeader
        breadcrumbs={[{ label: "Batch jobs", to: ROUTES.internal.batchJobs.path() }]}
        title={job.name}
        subtitle={`Created by ${job.createdBy} · ${new Date(job.createdAt).toLocaleString()}`}
        actions={
          <>
            {job.corpusFeedId && (
              <Chip
                component={Link}
                to={feedPath({ id: job.corpusFeedId })}
                icon={<RssFeedIcon />}
                label="Feed managed"
                size="small"
                clickable
              />
            )}
            <Chip label={label} size="small" variant="outlined" />
            <BatchJobStatusBadge status={job.status} />
            {retryable && (
              <RequireRole role="platform.admin">
                <Button variant="outlined" size="small" onClick={onRetryFailed}>
                  Retry failed items ({job.failedItemCount})
                </Button>
              </RequireRole>
            )}
            {deletable && (
              <RequireRole role="platform.admin">
                <Button variant="outlined" size="small" onClick={onDelete}>
                  Archive
                </Button>
              </RequireRole>
            )}
            {PAUSABLE_STATUSES.has(job.status) && (
              <RequireRole role="platform.admin">
                <Button variant="outlined" size="small" onClick={onPause}>
                  Pause
                </Button>
              </RequireRole>
            )}
            {job.status === "paused" && (
              <RequireRole role="platform.admin">
                <Button variant="outlined" size="small" onClick={onResume}>
                  Resume
                </Button>
              </RequireRole>
            )}
            {CANCELLABLE_STATUSES.has(job.status) && (
              <RequireRole role="platform.admin">
                <Button color="error" variant="outlined" size="small" onClick={onCancel}>
                  Cancel
                </Button>
              </RequireRole>
            )}
          </>
        }
      />

      <Stack gap={0.5}>
        <LinearProgress variant="determinate" value={progress} />
        <Typography variant="caption" color="text.secondary">
          {done}/{job.itemCount} items processed
          {job.openItemCount > 0 ? ` · ${job.openItemCount} still to process` : ""}
          {job.finishedAt
            ? ` · finished ${new Date(job.finishedAt).toLocaleString()} (${formatDuration(job.createdAt, job.finishedAt)})`
            : ""}
        </Typography>
      </Stack>

      {Panel ? (
        <Panel job={job} onJobChanged={refresh} />
      ) : (
        <Note>No panel registered for this job kind.</Note>
      )}

      <ConfirmDialog {...cancelDialogProps} />
    </InternalPage>
  );
}
